import { useState, useEffect, useMemo } from 'react'
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  detectCountryFromPhone,
  stripDialCode,
  buildE164,
  isValidPhone,
} from '../config/countries'

function Label({ children }) {
  return (
    <label className="block text-sm font-semibold text-brand-blue mb-1 font-body">
      {children}
    </label>
  )
}

/**
 * International phone input with country selector and national number entry.
 *
 * Props:
 * - value: E.164 string, e.g. +48123456789
 * - onChange: (value: string) => void
 * - label?: string
 * - placeholder?: string
 * - disabled?: boolean
 * - required?: boolean
 * - showValidation?: boolean
 */
export default function PhoneInput({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  required = false,
  showValidation = false,
}) {
  const [countryCode, setCountryCode] = useState(() =>
    detectCountryFromPhone(value, DEFAULT_COUNTRY)
  )

  const nationalNumber = useMemo(
    () => stripDialCode(value, countryCode),
    [value, countryCode]
  )

  useEffect(() => {
    setCountryCode(detectCountryFromPhone(value, countryCode))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value
    setCountryCode(nextCountry)
    const digits = nationalNumber.replace(/\D/g, '')
    onChange(buildE164(nextCountry, digits))
  }

  const handleNumberChange = (e) => {
    const raw = e.target.value
    // Allow only digits, spaces and dashes while typing, then strip to digits.
    const digits = raw.replace(/\D/g, '')
    onChange(buildE164(countryCode, digits))
  }

  const invalid = showValidation && value && !isValidPhone(value)

  return (
    <div className="space-y-1 font-body">
      {label && <Label>{label}</Label>}
      <div className="flex gap-3">
        <select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          className="shrink-0 bg-white rounded-xl px-3 py-2.5 text-base text-brand-blue border border-brand-blue/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold cursor-pointer"
          aria-label="Kod kraju"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dialCode}
            </option>
          ))}
        </select>
        <input
          type="tel"
          value={nationalNumber}
          onChange={handleNumberChange}
          placeholder={placeholder || '123 456 789'}
          disabled={disabled}
          required={required}
          className={`flex-1 bg-white rounded-xl px-4 py-2.5 text-base text-brand-blue placeholder-brand-blue/50 border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${
            invalid
              ? 'border-red-400 focus-visible:ring-red-300'
              : 'border-brand-blue/10'
          }`}
        />
      </div>
      {invalid && (
        <p className="text-sm text-red-600">
          Wprowadź poprawny numer telefonu.
        </p>
      )}
    </div>
  )
}

export { isValidPhone }
